import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../App";
import Layout from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Loader2, ShieldCheck, CheckCircle2, XCircle, AlertTriangle, MinusCircle } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../components/shared/PageHeader";

const STATUS_CONFIG = {
  met: { color: "#10B981", bg: "#F0FDF4", border: "#BBF7D0", icon: CheckCircle2, label: "Met" },
  partial: { color: "#F59E0B", bg: "#FFFBEB", border: "#FDE68A", icon: AlertTriangle, label: "Partial" },
  not_met: { color: "#DC2626", bg: "#FEF2F2", border: "#FECACA", icon: XCircle, label: "Not Met" },
  not_assessed: { color: "#94A3B8", bg: "#F8FAFC", border: "#E2E8F0", icon: MinusCircle, label: "Not Assessed" },
  not_applicable: { color: "#64748B", bg: "#F1F5F9", border: "#E2E8F0", icon: MinusCircle, label: "N/A" },
};

export default function ComplianceHubPage({ onLogout, userRole, isFeatureEnabled }) {
  const [frameworks, setFrameworks] = useState({});
  const [summary, setSummary] = useState({});
  const [activeFramework, setActiveFramework] = useState(null);
  const [fwData, setFwData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    try {
      const [fR, sR] = await Promise.allSettled([
        axios.get(`${API}/compliance-hub/frameworks`, { headers: getAuthHeaders() }),
        axios.get(`${API}/compliance-hub/summary`, { headers: getAuthHeaders() }),
      ]);
      if (fR.status === "fulfilled") setFrameworks(fR.value.data.frameworks || {});
      if (sR.status === "fulfilled") setSummary(sR.value.data || {});
    } catch {}
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const loadFramework = async (key) => {
    setActiveFramework(key);
    try {
      const resp = await axios.get(`${API}/compliance-hub/framework/${key}`, { headers: getAuthHeaders() });
      setFwData(resp.data);
    } catch { toast.error("Failed to load framework"); }
  };

  const assessReq = async (reqId, status) => {
    if (!activeFramework) return;
    try {
      await axios.put(`${API}/compliance-hub/assess`, { framework: activeFramework, requirement_id: reqId, status }, { headers: getAuthHeaders() });
      toast.success("Assessment saved");
      loadFramework(activeFramework);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (loading) return <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}><div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-[#2563EB]" size={28} /></div></Layout>;

  return (
    <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
      <div className="space-y-6" data-testid="compliance-hub-page">
        <PageHeader title="Regulatory Compliance Hub" subtitle="NQA-1, ITAR, NIST 800-171 gap analysis and compliance tracking" testId="compliance-hub-title" />

        {/* Framework Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="framework-cards">
          {Object.entries(frameworks).map(([key, fw]) => {
            const s = summary[key] || {};
            const score = s.score || 0;
            return (
              <div key={key} className={`bg-white border p-5 cursor-pointer transition-colors ${activeFramework === key ? "border-[#2563EB] bg-[#EFF6FF]" : "border-[#E2E8F0] hover:border-[#94A3B8]"}`} onClick={() => loadFramework(key)} data-testid={`fw-card-${key}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-bold text-[#0F172A]">{fw.name}</p>
                    <p className="text-[10px] text-[#64748B] mt-0.5">{fw.requirement_count} requirements</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-heading font-black" style={{ color: score >= 80 ? "#10B981" : score >= 50 ? "#F59E0B" : "#DC2626" }}>{score}%</p>
                  </div>
                </div>
                <div className="flex gap-1 mt-3">
                  {["met", "partial", "not_met", "not_assessed"].map(st => (
                    <div key={st} className="flex-1 h-2" style={{ backgroundColor: STATUS_CONFIG[st].border }} title={`${STATUS_CONFIG[st].label}: ${s[st === "not_assessed" ? "not_assessed" : st] || 0}`}>
                      <div className="h-full" style={{ backgroundColor: STATUS_CONFIG[st].color, width: `${((s[st === "not_assessed" ? "not_assessed" : st] || 0) / Math.max(s.total || 1, 1)) * 100}%` }} />
                    </div>
                  ))}
                </div>
                <div className="flex gap-3 mt-2 text-[9px]">
                  {[["met", s.met], ["partial", s.partial], ["not_met", s.not_met]].map(([st, v]) => (
                    <span key={st} style={{ color: STATUS_CONFIG[st].color }} className="font-bold">{v || 0} {STATUS_CONFIG[st].label}</span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Requirement Details */}
        {fwData && (
          <div className="bg-white border border-[#E2E8F0]" data-testid="requirement-list">
            <div className="px-4 py-3 border-b border-[#E2E8F0] bg-[#FAFAFA] flex items-center justify-between">
              <div>
                <p className="text-sm font-bold text-[#0F172A]">{fwData.name}</p>
                <p className="text-[10px] text-[#64748B]">{fwData.met}/{fwData.total} requirements met · Score: {fwData.score}%</p>
              </div>
              <ShieldCheck size={20} className="text-[#2563EB]" />
            </div>
            <div className="divide-y divide-[#F1F5F9]">
              {(fwData.requirements || []).map(r => {
                const cfg = STATUS_CONFIG[r.status] || STATUS_CONFIG.not_assessed;
                const Icon = cfg.icon;
                return (
                  <div key={r.id} className="px-4 py-3" style={{ backgroundColor: cfg.bg }} data-testid={`req-${r.id}`}>
                    <div className="flex items-start gap-3">
                      <Icon size={16} style={{ color: cfg.color }} className="mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs font-bold text-[#0F172A]">{r.id}</span>
                          <span className="text-sm font-medium text-[#0F172A]">{r.title}</span>
                        </div>
                        <p className="text-[11px] text-[#64748B] mt-0.5">{r.description}</p>
                        {r.assessed_at && <p className="text-[9px] text-[#94A3B8] mt-0.5">Last assessed: {new Date(r.assessed_at).toLocaleString()}</p>}
                      </div>
                      <div className="flex gap-1 shrink-0">
                        {["met", "partial", "not_met", "not_applicable"].map(st => (
                          <button key={st} onClick={() => assessReq(r.id, st)} className={`px-2 py-1 text-[9px] font-bold border transition-colors ${r.status === st ? "text-white" : ""}`} style={r.status === st ? { backgroundColor: STATUS_CONFIG[st].color, borderColor: STATUS_CONFIG[st].color } : { color: STATUS_CONFIG[st].color, borderColor: STATUS_CONFIG[st].border }} data-testid={`assess-${r.id}-${st}`}>
                            {STATUS_CONFIG[st].label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
